import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateVerification } from "@actions";
import { USER } from "@constants";
import { useTranslation } from "@helpers/translate";
import LanguageDropdown from "@components/layout/Header/components/LanguageDropdown";
import axios from "@helpers/axios/private.axios";

// Hooks
import { useProfileUserData } from "./hooks/useProfileUserData";
import { useAvatarUpload } from "./hooks/useAvatarUpload";
import { useCountries } from "./hooks/useCountries";
import { useProfileEditing } from "./hooks/useProfileEditing";

// Components
import { ProfileAvatar } from "./components/ProfileAvatar";
import { ProfileHello } from "./components/ProfileHello";
import { ProfileRow } from "./components/ProfileRow";

// Rows
import { NicknameRow } from "./components/rows/NicknameRow";
import { FirstNameRow } from "./components/rows/FirstNameRow";
import { LastNameRow } from "./components/rows/LastNameRow";
import { UserIdRow } from "./components/rows/UserIdRow";
import { EmailRow } from "./components/rows/EmailRow";
import { DateOfBirthRow } from "./components/rows/DateOfBirthRow";
import { PhoneRow } from "./components/rows/PhoneRow";
import { CountryRow } from "./components/rows/CountryRow";
import { VerificationRow } from "./components/rows/VerificationRow";
import { TimezoneRow } from "./components/rows/TimezoneRow";

import "./index.sass";

const COUNTRY_TO_TIMEZONE = {
  UA: "Europe/Kyiv",
  GB: "Europe/London",
  US: "America/New_York",
  DE: "Europe/Berlin",
  FR: "Europe/Paris",
  PL: "Europe/Warsaw",
  TR: "Europe/Istanbul",
  IN: "Asia/Kolkata",
  JP: "Asia/Tokyo",
  CN: "Asia/Shanghai",
};

export const ProfileTab = () => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();

  const reduxTimezone = useSelector((state) => state.user.user?.timezone || "");

  const {
    nickname,
    first_name,
    last_name,
    email,
    date_birth,
    country,
    phone,
    ondato_status,
    is_user_verified,
    kyc_submission,
    id,
    display_id,
    reduxAvatarUrl,
    locale,
    timezone,
  } = useProfileUserData();

  const {
    localAvatar,
    uploading,
    fileInputRef,
    handleUploadClick,
    handleFileChange,
  } = useAvatarUpload(reduxAvatarUrl);

  const { countries } = useCountries(locale);

  const {
    editingField,
    setEditingField,
    editingValues,
    setEditingValues,
    savingField,
    nicknameInputRef,
    firstNameInputRef,
    lastNameInputRef,
    phoneInputRef,
    dayInputRef,
    monthInputRef,
    yearInputRef,
    handleSaveField,
    handleSaveDateOfBirth,
  } = useProfileEditing(
    nickname,
    first_name,
    last_name,
    phone,
    date_birth,
    country,
    timezone,
    countries,
  );

  useEffect(() => {
    dispatch(updateVerification());
  }, [dispatch]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("tradingview.chartproperties");

      const parsed = raw ? JSON.parse(raw) : null;

      const tradingViewTimezone =
        typeof parsed?.timezone === "string" ? parsed.timezone : "";

      if (tradingViewTimezone && tradingViewTimezone !== reduxTimezone) {
        dispatch({
          type: USER.USER_FIELD,
          payload: {
            timezone: tradingViewTimezone,
          },
        });
      }
    } catch (error) {
      // Ignore invalid chartproperties.
    }
  }, [dispatch, reduxTimezone]);

  useEffect(() => {
    const getSessionCountryCode = async () => {
      try {
        const cachedGeo = localStorage.getItem("user_geo_data");

        if (cachedGeo) {
          const parsed = JSON.parse(cachedGeo);

          if (parsed?.country_code) {
            return String(parsed.country_code).toUpperCase();
          }
        }
      } catch (error) {
        // Ignore parse errors.
      }

      try {
        const response = await axios.get("/users/sessions");

        const sessions = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        const sessionWithCountry = sessions.find(
          (session) => session?.country_code || session?.countryCode,
        );

        const sessionCountry =
          sessionWithCountry?.country_code || sessionWithCountry?.countryCode;

        if (sessionCountry) {
          return String(sessionCountry).toUpperCase();
        }
      } catch (error) {
        // Sessions endpoint can be unavailable.
      }

      return "";
    };

    const resolveDefaultTimezone = async () => {
      if (timezone) return;

      const countryCode = await getSessionCountryCode();
      const timezoneFromCountry = COUNTRY_TO_TIMEZONE[countryCode];

      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const nextTimezone = timezoneFromCountry || browserTimezone || "UTC";

      if (nextTimezone) {
        handleSaveField("timezone", nextTimezone, {
          silent: true,
        });
      }
    };

    resolveDefaultTimezone();
  }, [timezone, handleSaveField]);

  return (
    <div className="profile-tab">
      <div className="profile-tab__avatar">
        <ProfileAvatar
          localAvatar={localAvatar}
          uploading={uploading}
          handleUploadClick={handleUploadClick}
          fileInputRef={fileInputRef}
          handleFileChange={handleFileChange}
        />

        <ProfileHello nickname={nickname} email={email} />
      </div>

      <div className="profile-tab__content">
        <div className="profile-tab__list">
          <h2>{__("profile.trader_profile")}</h2>

          <NicknameRow
            nickname={nickname}
            editingField={editingField}
            editingValues={editingValues}
            setEditingValues={setEditingValues}
            nicknameInputRef={nicknameInputRef}
            handleSaveField={handleSaveField}
            setEditingField={setEditingField}
            savingField={savingField}
          />

          <UserIdRow id={id} display_id={display_id} />

          <h2>{__("profile.personal_information")}</h2>

          <div className="profile-tab__required-field">
            <FirstNameRow
              firstName={first_name}
              editingField={editingField}
              editingValues={editingValues}
              setEditingValues={setEditingValues}
              firstNameInputRef={firstNameInputRef}
              handleSaveField={handleSaveField}
              setEditingField={setEditingField}
              savingField={savingField}
            />
          </div>

          <div className="profile-tab__required-field">
            <LastNameRow
              lastName={last_name}
              editingField={editingField}
              editingValues={editingValues}
              setEditingValues={setEditingValues}
              lastNameInputRef={lastNameInputRef}
              handleSaveField={handleSaveField}
              setEditingField={setEditingField}
              savingField={savingField}
            />
          </div>

          <div className="profile-tab__required-field">
            <DateOfBirthRow
              date_birth={date_birth}
              editingField={editingField}
              editingValues={editingValues}
              setEditingValues={setEditingValues}
              dayInputRef={dayInputRef}
              monthInputRef={monthInputRef}
              yearInputRef={yearInputRef}
              handleSaveDateOfBirth={handleSaveDateOfBirth}
              setEditingField={setEditingField}
              savingField={savingField}
            />
          </div>

          <EmailRow email={email} />

          <PhoneRow
            phone={phone}
            editingField={editingField}
            editingValues={editingValues}
            setEditingValues={setEditingValues}
            phoneInputRef={phoneInputRef}
            handleSaveField={handleSaveField}
            setEditingField={setEditingField}
            savingField={savingField}
          />

          <h2>{__("profile.verification")}</h2>

          <VerificationRow
            ondato_status={ondato_status}
            submission={kyc_submission}
            is_user_verified={is_user_verified}
            first_name={first_name}
            last_name={last_name}
            country={country}
            date_birth={date_birth}
          />

          <div className="profile-tab__required-field">
            <CountryRow
              country={country}
              locale={locale}
              countries={countries}
              handleSaveField={handleSaveField}
              savingField={savingField}
            />
          </div>

          <h2>{__("profile.settings")}</h2>

          <ProfileRow
            label={__("settings.language")}
            value=""
            actions={<LanguageDropdown />}
          />

          <TimezoneRow
            timezone={timezone}
            onSelectTimezone={(nextValue) =>
              handleSaveField("timezone", nextValue)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
